import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useBanner } from "../../../context/BannerContext";
import { track } from "../../../../clients/mixpanel";
import { withQtoLogging } from "../../../hooks/useQtoActions";
import { processOSTFile } from "../../../../integrations/qto/sources/ost/parser";
import { ostConditionsToAggregatedQto } from "../../../../integrations/qto/sources/ost/aggregate";
import type { AggregatedQto } from "../../../../integrations/qto/types";

export type OstState = "upload" | "loading" | "connected";

interface OstContextType {
  ostState: OstState;
  ostFileName: string | null;
  ostJobName: string | null;
  ostAggregatedQtos: AggregatedQto[] | null;
  ostTakeoffItemCount: number | null;
  ostLastUpdated: string | null;
  /** Resolves true when the file parsed and the session is connected. Never rejects. */
  uploadOstFile: (file: File) => Promise<boolean>;
  clearOstSession: () => void;
}

const OstContext = createContext<OstContextType | undefined>(undefined);

export const OstProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showBanner } = useBanner();
  const [ostState, setOstState] = useState<OstState>("upload");
  const [ostFileName, setOstFileName] = useState<string | null>(null);
  const [ostJobName, setOstJobName] = useState<string | null>(null);
  const [ostAggregatedQtos, setOstAggregatedQtos] = useState<AggregatedQto[] | null>(null);
  const [ostTakeoffItemCount, setOstTakeoffItemCount] = useState<number | null>(null);
  const [ostLastUpdated, setOstLastUpdated] = useState<string | null>(null);

  const clearOstSession = useCallback(() => {
    setOstState("upload");
    setOstFileName(null);
    setOstJobName(null);
    setOstAggregatedQtos(null);
    setOstTakeoffItemCount(null);
    setOstLastUpdated(null);
  }, []);

  const uploadOstFile = useCallback(
    async (file: File): Promise<boolean> => {
      // Captured before the state below moves: by the catch, ostState is "loading".
      const hadSession = ostState === "connected";
      setOstState("loading");
      try {
        await withQtoLogging(
          async () => {
            const xml = await file.text();
            const { conditions, jobName } = await processOSTFile(xml);
            const aggregated = ostConditionsToAggregatedQto(conditions);

            // Set on success, not before: a failed parse must not rename the session it replaced.
            setOstFileName(file.name);
            setOstJobName(jobName);
            setOstAggregatedQtos(aggregated);
            setOstTakeoffItemCount(aggregated.length);
            setOstLastUpdated(new Date().toISOString());
            setOstState("connected");

            void track("excel.qto.ost.fileUploaded", {
              fileName: file.name,
              jobName,
              conditionCount: conditions.length,
              aggregatedCount: aggregated.length,
            });
          },
          { context: "handleOstFileUpload", message: "Failed to parse OST file", fileName: file.name }
        );
        return true;
      } catch (error) {
        showBanner(error instanceof Error && error.message ? error.message : "Failed to parse OST file", "error");
        // A bad file replacing a connected source must leave it alone to retry — clearing drops the
        // job, its QTOs and the card, and since the datasource stays registered the drawer's tile is
        // disabled too, leaving no way back. Only a first upload has nothing to preserve.
        if (hadSession) setOstState("connected");
        else clearOstSession();
        return false;
      }
    },
    [showBanner, clearOstSession, ostState]
  );

  const value = useMemo(
    () => ({
      ostState,
      ostFileName,
      ostJobName,
      ostAggregatedQtos,
      ostTakeoffItemCount,
      ostLastUpdated,
      uploadOstFile,
      clearOstSession,
    }),
    [
      ostState,
      ostFileName,
      ostJobName,
      ostAggregatedQtos,
      ostTakeoffItemCount,
      ostLastUpdated,
      uploadOstFile,
      clearOstSession,
    ]
  );

  return <OstContext.Provider value={value}>{children}</OstContext.Provider>;
};

export const useOst = (): OstContextType => {
  const context = useContext(OstContext);
  if (!context) {
    throw new Error("useOst must be used within an OstProvider");
  }
  return context;
};
